/**
 * An erased member's page (box 17, 2026-10-31) — the strip that says so, and
 * the lock that switches off the record's editors.
 *
 * `delete_member` erases a member under GDPR Art. 17 and keeps the row as a
 * blank: migration 272 holds it (`members_hold_erasure`), and every action
 * that would write the member's record refuses with `member_erased`. Without
 * these two, the staff page would show a profile of dashes with no word of
 * why, and offer Saves the server refuses.
 *
 * In their own module, not inside `MemberDetail.tsx`, so the render test
 * imports them without the page's supabase/api graph.
 */

import type { JSX, ReactNode } from "react";

/**
 * The strip an erased member's page opens with. A state, not a warning —
 * neutral ink, not the flags' red. `erasedOn` is the date as the page
 * formats its other dates.
 */
export function ErasedNotice({
  erasedOn,
}: {
  readonly erasedOn: string;
}): JSX.Element {
  return (
    <div className="member-erased-notice" role="status">
      <strong>Erased on {erasedOn}</strong>
      <p>
        Their details were erased under GDPR (Art. 17), so this record can't be
        edited.
      </p>
    </div>
  );
}

/**
 * Switches off every control under it when `locked`. A disabled `<fieldset>`
 * disables every button, input and select it contains, and `display: contents`
 * (styles.css, `.member-record-lock`) keeps it out of the layout; the bare
 * Edit / Add / × affordances under it are hidden there too. Unlocked, the
 * children render as if it weren't there.
 */
export function RecordLock({
  locked,
  children,
}: {
  readonly locked: boolean;
  readonly children: ReactNode;
}): JSX.Element {
  if (!locked) return <>{children}</>;
  return (
    <fieldset className="member-record-lock" disabled>
      {children}
    </fieldset>
  );
}
